import {
  CreateScheduledQueryBody,
  ListScheduledQueriesParams,
  ScheduledQuery,
  scheduledQueries,
  scheduledQueriesV2,
  UpdateScheduledQueryBody,
} from '@api';
import { generatePath } from '@helpers';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';

const isPromptLibraryEnabled = () =>
  useFlags.getState().getFeatureFlagState('enable_prompt_library');

const getSchedulerRoutes = () =>
  isPromptLibraryEnabled() ? scheduledQueriesV2 : scheduledQueries;

export const listScheduledQueries = (
  params: ListScheduledQueriesParams = {}
) => {
  const routes = getSchedulerRoutes();
  const path = generatePath({
    basePath: routes.list.path,
    params: {
      ...(params.status && { status: params.status }),
      ...(params.page && { page: params.page }),
      ...(params.page_size && { page_size: params.page_size }),
    },
  });

  return wretchRequest(path, { schema: routes.list.schema });
};

export const getScheduledQuery = (id: string) => {
  const routes = getSchedulerRoutes();
  return wretchRequest(routes.get.path(id), {
    schema: routes.get.schema,
  });
};

export const listScheduledQueryConversations = (
  id: string,
  params: { page?: number; page_size?: number } = {}
) => {
  // Endpoint #9 lives in conversation service, not conversation-scheduler
  const path = generatePath({
    basePath: scheduledQueries.listConversations.path(id),
    params: {
      ...(params.page && { page: params.page }),
      ...(params.page_size && { page_size: params.page_size }),
    },
  });

  return wretchRequest(path, {
    schema: scheduledQueries.listConversations.schema,
  });
};

export const createScheduledQuery = (
  body: CreateScheduledQueryBody
): Promise<ScheduledQuery> => {
  const routes = getSchedulerRoutes();
  return wretchRequest(routes.create.path, {
    method: 'post',
    body,
    schema: routes.create.schema,
  });
};

export const updateScheduledQuery = (
  id: string,
  body: UpdateScheduledQueryBody
) =>
  wretchRequest(getSchedulerRoutes().update.path(id), {
    method: 'put',
    body,
  });

export const deleteScheduledQuery = (id: string) =>
  wretchRequest(getSchedulerRoutes().delete.path(id), {
    method: 'delete',
  });

export const suspendScheduledQuery = (id: string) =>
  wretchRequest(getSchedulerRoutes().suspend.path(id), {
    method: 'post',
    body: {},
  });

export const resumeScheduledQuery = (id: string) =>
  wretchRequest(getSchedulerRoutes().resume.path(id), {
    method: 'post',
    body: {},
  });

export const runScheduledQueryNow = (id: string) => {
  const routes = getSchedulerRoutes();
  return wretchRequest(routes.runNow.path(id), {
    method: 'post',
    body: {},
    schema: routes.runNow.schema,
  });
};
